import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  TextField,
  List,
  ListItem,
  ListItemButton,
  Paper,
} from '@mui/material';
import { useDebounce } from '../Utils/debounce';

interface PlaceDetails {
  name: string;
  lat: number;
  lng: number;
}

interface Props {
  onPlaceSelect: (place: PlaceDetails) => void;
  label?: string;
  placeholder?: string;
  value?: any;
}

const AutoCompletePlaces: React.FC<Props> = ({
  onPlaceSelect,
  label = 'Location',
  placeholder = 'Enter a location',
  value,
}) => {
  const [input, setInput] = useState(value?.name || '');
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debouncedInput = useDebounce(input, 300);

  // Sync input with value prop changes
  useEffect(() => {
    if (value?.name && value.name !== input) {
      setInput(value.name);
    }
  }, [value?.name]);

  // Check if Google Maps is loaded and initialize services
  useEffect(() => {
    const checkGoogleMaps = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        if (!autocompleteService.current) {
          try {
            autocompleteService.current = new google.maps.places.AutocompleteService();
          } catch (error) {
            console.error('Error initializing AutocompleteService:', error);
            return;
          }
        }
        if (!placesService.current && mapRef.current) {
          try {
            const map = new window.google.maps.Map(mapRef.current);
            placesService.current = new google.maps.places.PlacesService(map);
          } catch (error) {
            console.error('Error initializing PlacesService:', error);
            return;
          }
        }
        setIsGoogleMapsLoaded(true);
      } else {
        setIsGoogleMapsLoaded(false);
        // Retry after a short delay if Google Maps is not loaded yet
        setTimeout(checkGoogleMaps, 100);
      }
    };

    checkGoogleMaps();
  }, []);

  // Fetch predictions when debounced input changes
  useEffect(() => {
    if (!isGoogleMapsLoaded || !autocompleteService.current) {
      return;
    }

    if (debouncedInput && debouncedInput.trim().length > 0) {
      autocompleteService.current.getPlacePredictions(
        { input: debouncedInput },
        (predictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            setPredictions(predictions);
            // Show predictions if input is focused (user is actively typing)
            if (isFocused) {
              setShowPredictions(true);
            }
          } else {
            // Handle different error statuses
            if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
              setPredictions([]);
              if (isFocused) {
                setShowPredictions(false);
              }
            } else if (status === google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT) {
              console.warn('Google Places API: Over query limit');
              setPredictions([]);
            } else if (status === google.maps.places.PlacesServiceStatus.REQUEST_DENIED) {
              console.error('Google Places API: Request denied. Check API key and billing.');
              setPredictions([]);
            } else if (status === google.maps.places.PlacesServiceStatus.INVALID_REQUEST) {
              console.warn('Google Places API: Invalid request');
              setPredictions([]);
            } else {
              setPredictions([]);
            }
          }
        }
      );
    } else {
      setPredictions([]);
      if (!debouncedInput || debouncedInput.trim().length === 0) {
        setShowPredictions(false);
      }
    }
  }, [debouncedInput, isGoogleMapsLoaded, isFocused]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInput(value);
    // Hide predictions immediately if input is cleared
    if (!value || value.trim().length === 0) {
      setPredictions([]);
      setShowPredictions(false);
    }
    // Don't auto-show predictions when typing - only show on click/focus
  };

  const handlePredictionSelect = useCallback((prediction: google.maps.places.AutocompletePrediction) => {
    if (!placesService.current) {
      console.error('PlacesService not initialized');
      return;
    }

    placesService.current.getDetails(
      { placeId: prediction.place_id },
      (place, status) => {
        if (
          status === google.maps.places.PlacesServiceStatus.OK &&
          place &&
          place.geometry?.location
        ) {
          const placeDetails: PlaceDetails = {
            name: place.name || prediction.description,
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          };
          setInput(placeDetails.name);
          setShowPredictions(false);
          setPredictions([]);
          onPlaceSelect(placeDetails);
        } else {
          console.error('Error fetching place details:', status);
        }
      }
    );
  }, [onPlaceSelect]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowPredictions(false);
      }
    };

    if (showPredictions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showPredictions]);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div ref={mapRef} style={{ display: 'none' }} />
      <TextField
        fullWidth
        label={label}
        value={input}
        onChange={handleInputChange}
        placeholder={placeholder}
        onClick={() => {
          setIsFocused(true);
          // Show predictions when clicked, if we have them and input has value
          if (predictions.length > 0 && input.trim().length > 0) {
            setShowPredictions(true);
          }
        }}
        onFocus={() => {
          setIsFocused(true);
          // Show predictions when focused, if we have them and input has value
          if (predictions.length > 0 && input.trim().length > 0) {
            setShowPredictions(true);
          }
        }}
        onBlur={() => {
          // Don't immediately hide on blur - let click outside handler manage it
          // This prevents dropdown from closing when clicking on a prediction
          setTimeout(() => setIsFocused(false), 200);
        }}
      />
      {showPredictions && predictions.length > 0 && (
        <Paper
          style={{
            position: 'absolute',
            width: '100%',
            zIndex: 1000,
            marginTop: '4px',
            maxHeight: '300px',
            overflow: 'auto',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          }}
        >
          <List dense>
            {predictions.map((prediction) => (
              <ListItem key={prediction.place_id} disablePadding>
                <ListItemButton 
                  onClick={() => handlePredictionSelect(prediction)}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <div style={{ width: '100%' }}>
                    <div style={{ fontWeight: 500 }}>
                      {prediction.structured_formatting.main_text}
                    </div>
                    <div style={{ fontSize: '0.8em', color: '#666', marginTop: '2px' }}>
                      {prediction.structured_formatting.secondary_text}
                    </div>
                  </div>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </div>
  );
};

export default AutoCompletePlaces;
